import { supabase } from '@/lib/supabaseClient';
import type { CopyCategory } from '@/lib/copyCategoryDependencies';
import { COPY_CATEGORIES as COPY_CATEGORY_DEFS } from '@/lib/copyCategoryDependencies';
import { withTimeout, isOperationTimeout } from '@/lib/withTimeout';

/**
 * How long to wait for `create_sub_account` before reporting that it did not answer.
 *
 * `statement_timeout` for `authenticated` is 8s, so the server cannot legitimately hold a
 * creation open past that plus transport. This is well clear of the honest worst case and
 * well inside a user's patience.
 */
const CREATE_SUB_ACCOUNT_TIMEOUT_MS = 20_000;

export interface Tenant {
  tenant_id: string;
  company_name: string;
  contact_name: string;
  contact_phone?: string | null;
  contact_email: string;
  email_config?: any;
  created_at: string;
  updated_at: string;
}

export interface CreateTenantData {
  company_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone?: string;
}

/** Clone-create payload (TEN-1.4): a normal create plus the source to clone from. */
export interface CloneTenantData extends CreateTenantData {
  clone_from_tenant_id: string;
}

export interface TenantCloneWarnings {
  /** notification_type values whose cloned template body still contains the source company name. */
  notification_literal_name?: string[];
}

export interface CreateTenantResponse {
  success: boolean;
  tenant?: Tenant;
  error?: string;
  message?: string;
  /** Present on clone-create (TEN-1.4); non-blocking review hints. */
  warnings?: TenantCloneWarnings;
  /** Source tenant id when this tenant was created by cloning. */
  cloned_from?: string;
}

/**
 * Creates a new tenant with default initialization
 * @param data Tenant creation data
 * @returns Response with tenant details or error
 */
export async function createTenant(
  data: CreateTenantData
): Promise<CreateTenantResponse> {
  try {
    // Validate input
    if (!data.company_name || data.company_name.trim() === '') {
      return {
        success: false,
        error: 'Company name is required',
      };
    }

    if (!data.contact_name || data.contact_name.trim() === '') {
      return {
        success: false,
        error: 'Contact name is required',
      };
    }

    if (!data.contact_email || data.contact_email.trim() === '') {
      return {
        success: false,
        error: 'Contact email is required',
      };
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.contact_email)) {
      return {
        success: false,
        error: 'Invalid email format',
      };
    }

    // Call database function to create tenant
    const { data: result, error } = await supabase.rpc('create_tenant', {
      p_company_name: data.company_name,
      p_contact_name: data.contact_name,
      p_contact_email: data.contact_email,
      p_contact_phone: data.contact_phone || null,
    });

    if (error) {
      console.error('[tenantService] Error creating tenant:', error);
      return {
        success: false,
        error: error.message,
      };
    }

    // Check if result indicates success
    if (result && result.success) {
      return {
        success: true,
        tenant: result.tenant,
        message: result.message || 'Tenant created successfully',
      };
    } else {
      return {
        success: false,
        error: result?.error || 'Failed to create tenant',
        message: result?.message,
      };
    }
  } catch (error: any) {
    console.error('[tenantService] Exception creating tenant:', error);
    return {
      success: false,
      error: error.message || 'An unexpected error occurred',
    };
  }
}

/**
 * Creates a new tenant by CLONING an existing source tenant's setup
 * (pipelines, stages, checklists, notification templates, theme, dashboards/widgets).
 * Invokes the 5-arg `create_tenant` overload (TEN-1.3). No users/projects are copied.
 * @param data Tenant creation data + `clone_from_tenant_id`
 * @returns Response with tenant details, optional `warnings`, or error
 */
export async function cloneTenant(
  data: CloneTenantData
): Promise<CreateTenantResponse> {
  try {
    if (!data.company_name || data.company_name.trim() === '') {
      return { success: false, error: 'Company name is required' };
    }
    if (!data.contact_name || data.contact_name.trim() === '') {
      return { success: false, error: 'Contact name is required' };
    }
    if (!data.contact_email || data.contact_email.trim() === '') {
      return { success: false, error: 'Contact email is required' };
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.contact_email)) {
      return { success: false, error: 'Invalid email format' };
    }
    if (!data.clone_from_tenant_id) {
      return { success: false, error: 'A source tenant to clone from is required' };
    }

    // Calls the 5-arg create_tenant overload (resolved by the extra named arg).
    const { data: result, error } = await supabase.rpc('create_tenant', {
      p_company_name: data.company_name,
      p_contact_name: data.contact_name,
      p_contact_email: data.contact_email,
      p_contact_phone: data.contact_phone || null,
      p_clone_from_tenant_id: data.clone_from_tenant_id,
    });

    if (error) {
      console.error('[tenantService] Error cloning tenant:', error);
      return { success: false, error: error.message };
    }

    if (result && result.success) {
      return {
        success: true,
        tenant: result.tenant,
        warnings: result.warnings,
        cloned_from: result.cloned_from,
        message: result.message || 'Tenant cloned successfully',
      };
    }
    return {
      success: false,
      error: result?.error || 'Failed to clone tenant',
      message: result?.message,
    };
  } catch (error: any) {
    console.error('[tenantService] Exception cloning tenant:', error);
    return {
      success: false,
      error: error.message || 'An unexpected error occurred',
    };
  }
}

/**
 * SUB-3.1 · FR20, FR21, FR4, ISO10. An Agency Admin creates a client account
 * under their own account.
 *
 * ⚠️ NOTE WHAT IS ABSENT FROM THIS PAYLOAD: there is no parent, and no clone
 * source. Both are derived server-side from `home_tenant_id()`, which is what
 * makes FR24/ISO10 hold structurally rather than by a validation a future caller
 * could forget. If you find yourself adding a `parent_tenant_id` field here, read
 * the header of `20260813b_sub_3_1_create_sub_account.sql` first: a
 * caller-supplied filter is not a filter, and that is finding F-1.
 */
/**
 * SUB-3.2 / FR22 and SUB-3.4 / FR26. Re-exported, not defined here.
 *
 * ⚠️ THE DEFINITIONS MOVED TO `@/lib/copyCategoryDependencies` and this file keeps
 * re-exporting them so no call site had to change. The move was not tidying: the
 * dependency rules were reachable only through a component, so the only way to check
 * them was to open the dialog in a browser and look. They now have
 * `copyCategoryDependencies.test.ts`. Import from the lib in new code.
 *
 * ⚠️ THE TOKENS MUST MATCH `clone_tenant_config`'s VOCABULARY EXACTLY. That function
 * raises `22023` on an unknown token rather than ignoring it, deliberately, so a typo
 * surfaces as a refusal instead of an account quietly missing a category. Do not
 * "fix" a rejected token by making the server lenient.
 */
export {
  COPY_CATEGORIES,
  COPY_CATEGORY_DEPENDENCIES,
  COPY_CATEGORY_DEPENDENCY_REASONS,
  findUnmetDependencies,
  selectionIsCoherent,
  shouldWarnAboutSparseAccount,
} from '@/lib/copyCategoryDependencies';
export type { CopyCategory, UnmetDependency } from '@/lib/copyCategoryDependencies';


export interface CreateSubAccountData {
  company_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone?: string;
  /**
   * FR22 / FR23. Omitted or empty means START CLEAN, which is platform defaults
   * only. A non-empty selection copies exactly those categories from the parent
   * and nothing else.
   *
   * ⚠️ EMPTY AND ALL SIX ARE DIFFERENT INSTRUCTIONS, and neither is "the default".
   * "Copy everything" sends all six tokens explicitly rather than relying on an
   * absent value, because `clone_tenant_config` reads a null third argument as
   * every category while `create_sub_account` reads a null one as start clean. The
   * two defaults are each pinned by their own existing callers; sending the array
   * explicitly means this client never depends on either.
   */
  categories?: CopyCategory[];
}

export interface CreateSubAccountResponse {
  success: boolean;
  tenant?: Tenant & { parent_tenant_id?: string };
  /**
   * `start_clean`, `copy_all` or `copy_selected`, decided by the server and stated
   * rather than inferred, so the UI cannot report a copy that did not happen.
   */
  copy_mode?: string;
  /**
   * Whether copying from the parent is available AT ALL. True since SUB-3.2. Read
   * it rather than hardcoding: the capability depends on server-side authorization,
   * so the server is the only thing that can answer honestly.
   */
  copy_from_parent_available?: boolean;
  /**
   * What the server actually copied. Null on the start-clean path. Read this rather
   * than echoing the request: they differ if anything server-side declines a
   * category, and the point of the field is to be the truth.
   */
  copied_categories?: string[];
  /** The parent's usage AFTER the creation, so FR5's count needs no second call. */
  usage?: unknown;
  error?: string;
  /** SQLSTATE from PostgREST. '42501' is an authority refusal, '23514' a rule. */
  code?: string;
}

export async function createSubAccount(
  data: CreateSubAccountData
): Promise<CreateSubAccountResponse> {
  // Client-side validation is a courtesy that saves a round trip, never a control:
  // create_sub_account revalidates all of it, and does so BEHIND its authority
  // gate so an unauthorized caller learns nothing about the input shape.
  if (!data.company_name?.trim()) {
    return { success: false, error: 'Company name is required' };
  }
  if (!data.contact_name?.trim()) {
    return { success: false, error: 'Contact name is required' };
  }
  if (!data.contact_email?.trim()) {
    return { success: false, error: 'Contact email is required' };
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(data.contact_email)) {
    return { success: false, error: 'Invalid email format' };
  }

  // SUB-3.2. Same courtesy, same non-status: the server owns this vocabulary and
  // raises 22023 on anything it does not recognise. Checked here only so a client
  // bug reads as a client bug instead of arriving as a database error.
  const known = new Set<string>(COPY_CATEGORY_DEFS.map((c) => c.id));
  const unknown = (data.categories ?? []).filter((c) => !known.has(c));
  if (unknown.length > 0) {
    return { success: false, error: `Unknown copy category: ${unknown.join(', ')}` };
  }

  try {
    /**
     * ⚠️ BOUNDED, because an unbounded await here is what a stuck auth lock turns into a
     * permanent spinner. `CreateSubAccountDialog` clears `submitting` in a `finally`,
     * which is correct and still never runs if the promise does not settle. Reported
     * 2026-08-18: a client sat on "Creating…" for 30 minutes while the API logs showed no
     * `create_sub_account` request had ever been made.
     *
     * The cause is fixed in `contexts/AuthContext.tsx`; this is the guard that stops the
     * same shape of bug from ever being invisible again.
     *
     * The budget is deliberately generous. `create_sub_account` on the copy path runs
     * `clone_tenant_config` across six categories, and `statement_timeout` for
     * `authenticated` is 8s, so the server cannot legitimately take longer than that plus
     * transport. Anything past 20s is not a slow creation, it is a call that is not
     * running at all.
     */
    const { data: result, error } = await withTimeout(
      supabase.rpc('create_sub_account', {
        p_company_name: data.company_name,
        p_contact_name: data.contact_name,
        p_contact_email: data.contact_email,
        p_contact_phone: data.contact_phone?.trim() || null,
        // FR22 / FR23. Null for start clean, an explicit array otherwise, including
        // for "copy everything". Never relies on a server-side default: see the note
        // on `CreateSubAccountData.categories` for why the two functions read a null
        // third argument differently.
        p_categories:
          data.categories && data.categories.length > 0 ? data.categories : null,
      }),
      {
        timeoutMs: CREATE_SUB_ACCOUNT_TIMEOUT_MS,
        label: 'create_sub_account',
        onTimeout: (label, timeoutMs) => {
          console.error(
            `[tenantService] ${label} did not answer within ${timeoutMs}ms. The request ` +
              'may never have been sent: a stuck Supabase auth Web Lock blocks the session ' +
              'lookup that happens before fetch. Check navigator.locks.query() for ' +
              '"lock:solerro-pm-auth" and see contexts/AuthContext.tsx.'
          );
        },
      }
    );

    // ⚠️ THE ERROR CHANNEL IS THE ERROR CHANNEL HERE, unlike create_tenant.
    //
    // `create_tenant` ends in `EXCEPTION WHEN OTHERS THEN RETURN
    // json_build_object('success', false, ...)`, so a failed creation arrives as
    // HTTP 200 and `createTenant` above has to inspect the payload to notice.
    // `create_sub_account` raises instead, which is why this checks `error` and
    // why the SQLSTATE is passed through: the caller can tell an authority refusal
    // (42501) from a rule (23514) from a name collision (23505).
    if (error) {
      console.error('[tenantService] create_sub_account refused:', error);
      return {
        success: false,
        error: error.message || 'Failed to create the client account',
        code: (error as { code?: string }).code,
      };
    }

    // A 200 whose shape we cannot read is not a success. Saying "created" on an
    // unreadable payload would leave the user unable to find the account they were
    // just told exists.
    const payload = result as Record<string, unknown> | null;
    const tenant = payload?.tenant as (Tenant & { parent_tenant_id?: string }) | undefined;
    if (payload?.status !== 'ok' || !tenant?.tenant_id) {
      return {
        success: false,
        error: 'The server returned a response this build cannot read. The account may or may not have been created; reload the sub-accounts list before retrying.',
      };
    }

    return {
      success: true,
      tenant,
      copy_mode: payload.copy_mode as string | undefined,
      copy_from_parent_available:
        payload.copy_from_parent_available === true,
      // The server's answer, not an echo of the request. Narrowed rather than cast:
      // an unreadable shape becomes undefined, which the caller already treats as
      // "nothing to report", instead of a string[] that is not one.
      copied_categories: Array.isArray(payload.copied_categories)
        ? (payload.copied_categories as unknown[]).filter(
            (c): c is string => typeof c === 'string',
          )
        : undefined,
      usage: payload.usage,
    };
  } catch (e: unknown) {
    console.error('[tenantService] Exception creating sub-account:', e);

    // ⚠️ A TIMEOUT IS "NO ANSWER", NEVER "IT DID NOT HAPPEN", and the copy has to say so.
    // The request may have been sent, committed, and had its reply lost. Telling the user
    // it failed would invite a retry that creates a second client account, which
    // `create_sub_account`'s name-collision rule would catch only while the names match
    // exactly. Directing them to reload the list first is the only honest instruction.
    if (isOperationTimeout(e)) {
      return {
        success: false,
        error:
          'The server did not answer in time, so we cannot tell whether the client account was created. Reload the client accounts list before trying again, and if it is there, the creation succeeded.',
      };
    }

    return {
      success: false,
      error: e instanceof Error ? e.message : 'An unexpected error occurred',
    };
  }
}

export interface SeedTestDataResponse {
  success: boolean;
  projects_created?: number;
  documents_attached?: number;
  by_stage?: Record<string, number>;
  error?: string;
}

/**
 * Provisions synthetic test users + test projects (+ sample documents) for a
 * tenant via the Python backend (TEN-2.4). Separate from tenant create/clone so
 * a slow/failed seed leaves a valid tenant.
 * @param tenantId Target tenant
 * @param count Number of test projects (clamped 1..100 server-side)
 */
export async function seedTestData(
  tenantId: string,
  count: number
): Promise<SeedTestDataResponse> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const baseUrl = import.meta.env.VITE_PYTHON_BACKEND_URL || 'http://localhost:8000';
    const response = await fetch(
      `${baseUrl}/api/v1/tenants/${tenantId}/seed-test-data`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ count }),
      }
    );

    const result = await response.json().catch(() => ({}));
    if (!response.ok || result?.success === false) {
      return {
        success: false,
        error: result?.detail || result?.error || `Seeding failed (HTTP ${response.status})`,
      };
    }
    return {
      success: true,
      projects_created: result.projects_created,
      documents_attached: result.documents_attached,
      by_stage: result.by_stage,
    };
  } catch (error: any) {
    console.error('[tenantService] Exception seeding test data:', error);
    return { success: false, error: error.message || 'Seeding request failed' };
  }
}

export interface TestDataSummary {
  projects: number;
  users: number;
}

/**
 * Counts a tenant's seeded test data (synthetic-owned TEST projects + @test.local
 * users) so the UI can show the "Remove test data" affordance only when relevant.
 */
export async function getTestDataSummary(tenantId: string): Promise<TestDataSummary> {
  try {
    const { data, error } = await supabase.rpc('tenant_test_data_summary', {
      p_tenant_id: tenantId,
    });
    if (error || !data) return { projects: 0, users: 0 };
    return { projects: data.projects ?? 0, users: data.users ?? 0 };
  } catch {
    return { projects: 0, users: 0 };
  }
}

export interface CleanupTestDataResponse {
  success: boolean;
  projects_deleted?: number;
  documents_deleted?: number;
  users_deleted?: number;
  storage_objects_removed?: number;
  error?: string;
}

/**
 * Removes all seeded TEST data for a tenant (TEN-2.5), via the Python backend so
 * the orphaned per-project sample-document copies are removed from storage too:
 * deletes TEST projects (+ cascade) and synthetic @test.local users (SQL RPC),
 * then removes their storage objects. Real config is left intact.
 * @param tenantId Target tenant
 */
export async function cleanupTestData(
  tenantId: string
): Promise<CleanupTestDataResponse> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const baseUrl = import.meta.env.VITE_PYTHON_BACKEND_URL || 'http://localhost:8000';
    const response = await fetch(
      `${baseUrl}/api/v1/tenants/${tenantId}/cleanup-test-data`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
      }
    );

    const result = await response.json().catch(() => ({}));
    if (!response.ok || result?.success === false) {
      return {
        success: false,
        error: result?.detail || result?.error || `Failed to remove test data (HTTP ${response.status})`,
      };
    }
    return {
      success: true,
      projects_deleted: result.projects_deleted,
      documents_deleted: result.documents_deleted,
      users_deleted: result.users_deleted,
      storage_objects_removed: result.storage_objects_removed,
    };
  } catch (error: any) {
    console.error('[tenantService] Exception removing test data:', error);
    return { success: false, error: error.message || 'Request failed' };
  }
}

/**
 * Lists all tenants (superadmin only)
 * @returns Array of tenants
 */
export async function listTenants(): Promise<Tenant[]> {
  try {
    const { data, error } = await supabase
      .from('tenants')
      .select('*')
      .order('company_name');

    if (error) {
      console.error('[tenantService] Error listing tenants:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('[tenantService] Exception listing tenants:', error);
    throw error;
  }
}

/**
 * Gets details for a specific tenant
 * @param tenantId The tenant UUID
 * @returns Tenant details or null if not found
 */
export async function getTenantDetails(
  tenantId: string
): Promise<Tenant | null> {
  try {
    const { data, error } = await supabase
      .from('tenants')
      .select('*')
      .eq('tenant_id', tenantId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // Not found
        return null;
      }
      console.error('[tenantService] Error getting tenant details:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('[tenantService] Exception getting tenant details:', error);
    throw error;
  }
}

/**
 * Updates tenant information
 * @param tenantId The tenant UUID
 * @param updates Partial tenant data to update
 * @returns True if update was successful
 */
export async function updateTenant(
  tenantId: string,
  updates: Partial<Omit<Tenant, 'tenant_id' | 'created_at' | 'updated_at'>>
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('tenants')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('tenant_id', tenantId);

    if (error) {
      console.error('[tenantService] Error updating tenant:', error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('[tenantService] Exception updating tenant:', error);
    throw error;
  }
}

export interface ArchiveTenantResponse {
  success: boolean;
  tenant_id?: string;
  company_name?: string;
  archived_at?: string;
  archived_by?: string;
  reason?: string;
  preserved_data?: {
    projects: number;
    pipelines: number;
    users_affected: number;
  };
  error?: string;
  message?: string;
}

export interface UnarchiveTenantResponse {
  success: boolean;
  tenant_id?: string;
  company_name?: string;
  restored_at?: string;
  users_reactivated?: number;
  error?: string;
  message?: string;
}

export interface ArchivedTenant {
  tenant_id: string;
  company_name: string;
  contact_email: string;
  archived_at: string;
  archived_by_email: string | null;
  project_count: number;
  user_count: number;
}

export interface HardDeleteTenantResponse {
  success: boolean;
  tenant_id?: string;
  company_name?: string;
  deleted?: {
    projects: number;
    pipelines: number;
    stages: number;
    checklist_templates: number;
    notification_templates: number;
    teams: number;
    user_roles: number;
  };
  error?: string;
  message?: string;
}

/**
 * Archives a tenant (soft delete) - removes access but preserves all data
 * This will:
 * - Mark tenant as archived
 * - Deactivate all user role assignments (removes access)
 * - Clear active superadmin sessions
 * - Preserve ALL data (projects, pipelines, stages, etc.)
 *
 * Can be restored later using unarchiveTenant()
 *
 * @param tenantId The tenant UUID
 * @param reason Optional reason for archiving
 * @returns Response with archive details or error
 */
export async function archiveTenant(
  tenantId: string,
  reason?: string
): Promise<ArchiveTenantResponse> {
  try {
    const { data: result, error } = await supabase.rpc('archive_tenant', {
      p_tenant_id: tenantId,
      p_reason: reason || null,
    });

    if (error) {
      console.error('[tenantService] Error archiving tenant:', error);
      return {
        success: false,
        error: error.message,
      };
    }

    if (result && result.success) {
      return {
        success: true,
        tenant_id: result.tenant_id,
        company_name: result.company_name,
        archived_at: result.archived_at,
        archived_by: result.archived_by,
        reason: result.reason,
        preserved_data: result.preserved_data,
        message: result.message || 'Tenant archived successfully',
      };
    } else {
      return {
        success: false,
        error: result?.error || 'Failed to archive tenant',
        message: result?.message,
      };
    }
  } catch (error: any) {
    console.error('[tenantService] Exception archiving tenant:', error);
    return {
      success: false,
      error: error.message || 'An unexpected error occurred',
    };
  }
}

/**
 * Restores an archived tenant (unarchive)
 * This will:
 * - Remove archived flag
 * - Reactivate all user role assignments
 * - Restore full access to the tenant
 *
 * @param tenantId The tenant UUID
 * @returns Response with restore details or error
 */
export async function unarchiveTenant(
  tenantId: string
): Promise<UnarchiveTenantResponse> {
  try {
    const { data: result, error } = await supabase.rpc('unarchive_tenant', {
      p_tenant_id: tenantId,
    });

    if (error) {
      console.error('[tenantService] Error unarchiving tenant:', error);
      return {
        success: false,
        error: error.message,
      };
    }

    if (result && result.success) {
      return {
        success: true,
        tenant_id: result.tenant_id,
        company_name: result.company_name,
        restored_at: result.restored_at,
        users_reactivated: result.users_reactivated,
        message: result.message || 'Tenant restored successfully',
      };
    } else {
      return {
        success: false,
        error: result?.error || 'Failed to unarchive tenant',
        message: result?.message,
      };
    }
  } catch (error: any) {
    console.error('[tenantService] Exception unarchiving tenant:', error);
    return {
      success: false,
      error: error.message || 'An unexpected error occurred',
    };
  }
}

/**
 * Lists all archived tenants with metadata
 * @returns Array of archived tenants
 */
export async function listArchivedTenants(): Promise<ArchivedTenant[]> {
  try {
    const { data, error } = await supabase.rpc('list_archived_tenants');

    if (error) {
      console.error('[tenantService] Error listing archived tenants:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('[tenantService] Exception listing archived tenants:', error);
    throw error;
  }
}

/**
 * PERMANENTLY deletes a tenant and ALL associated data (use with EXTREME caution)
 * This will permanently delete:
 * - All projects
 * - All pipelines and stages
 * - All checklist templates
 * - All notification templates
 * - All teams
 * - All user role assignments
 * - Theme settings
 * - The tenant record itself
 *
 * ⚠️ WARNING: This CANNOT be undone. Consider using archiveTenant() instead.
 *
 * @param tenantId The tenant UUID
 * @returns Response with deletion details or error
 */
export async function hardDeleteTenant(
  tenantId: string
): Promise<HardDeleteTenantResponse> {
  try {
    // Call database function to permanently delete tenant and all associated data
    const { data: result, error } = await supabase.rpc('hard_delete_tenant', {
      p_tenant_id: tenantId,
    });

    if (error) {
      console.error('[tenantService] Error hard deleting tenant:', error);
      return {
        success: false,
        error: error.message,
      };
    }

    // Check if result indicates success
    if (result && result.success) {
      return {
        success: true,
        tenant_id: result.tenant_id,
        company_name: result.company_name,
        deleted: result.deleted,
        message: result.message || 'Tenant permanently deleted',
      };
    } else {
      return {
        success: false,
        error: result?.error || 'Failed to delete tenant',
        message: result?.message,
      };
    }
  } catch (error: any) {
    console.error('[tenantService] Exception hard deleting tenant:', error);
    return {
      success: false,
      error: error.message || 'An unexpected error occurred',
    };
  }
}

/**
 * Checks if a company name is already taken
 * @param companyName The company name to check
 * @returns True if the name is available, false if taken
 */
export async function isCompanyNameAvailable(
  companyName: string
): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('tenants')
      .select('tenant_id')
      .ilike('company_name', companyName)
      .limit(1);

    if (error) {
      console.error('[tenantService] Error checking company name:', error);
      throw error;
    }

    return !data || data.length === 0;
  } catch (error) {
    console.error('[tenantService] Exception checking company name:', error);
    throw error;
  }
}
